import React from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useLocation } from "react-router-dom";

interface AppBarProps {
  isPartnership?: boolean;
}

const StyledAppBar = styled(AppBar)<AppBarProps>(
  ({ theme, isPartnership }) => ({
    backgroundColor: isPartnership ? "#ffffff" : "transparent",
    color: "#333333",
    boxShadow: "none",
    position: isPartnership ? "relative" : "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,

    [theme.breakpoints.up("lg")]: {
      backgroundColor: "#ffffff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      position: "sticky",
    },
  })
);

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    justifyContent: 'space-between',
    minHeight: '80px',
    paddingLeft: '25px !important',
    paddingRight: '25px !important',
    [theme.breakpoints.up('lg')]: {
        paddingLeft: '60px !important',
        paddingRight: '60px !important',
    },
}));

const Logo = styled("div")(() => ({
  fontSize: "24px",
  fontWeight: "bold",
  color: "#333333",
  cursor: "pointer",
}));

const MenuIconContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}));

const menuItems = [
  {label:"Why Digital Clone?", to:"/#why-digital-clone"},
  {label:"Case Studies", to:"/#case-studies"},
  {label:"Integration", to:"/#integration"},
  {label:"Partnership", to:"/#partnership "},
  {label:"Prices", to:"/#pricing"},
];

export default function Header() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { pathname } = useLocation();
  const isPartnership = pathname === "/partnership";

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const handleContactClick = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <StyledAppBar elevation={0} isPartnership={isPartnership}>
        <StyledToolbar>
          <MenuIconContainer>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </MenuIconContainer>

          <ListItemButton
            component="a"
            href="/"
            sx={{
              maxWidth: "180px",
              "&:hover": {
                color: "#000000",
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            <Logo>MeetMyBot</Logo>
          </ListItemButton>

          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {menuItems.map(({ label, to }) => (
              <Button
                key={label}
                href={to}
                sx={{
                  color: "#333333",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  px: 1.5,
                  py: 1,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: "#000000",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                {label}
              </Button>
            ))}

            <Button
              variant="contained"
              onClick={handleContactClick}
              sx={{
                backgroundColor: "#6366f1",
                borderRadius: "25px",
                padding: "10px 24px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#5048e5",
                  boxShadow: "none",
                },
              }}
            >
              Contact Us
            </Button>
          </Box>

          <Box
            sx={{ display: { xs: "block", lg: "none" }, width: "48px" }}
          ></Box>
        </StyledToolbar>
      </StyledAppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerToggle}>
        <Box
          sx={{ width: 280, pt: 2 }}
          role="presentation"
          onClick={handleDrawerToggle}
        >
          <List>
            {menuItems.map(({ label, to }) => (
              <ListItem key={label} disablePadding>
                <ListItemButton component="a" href={to} sx={{ py: 1.5, px: 3 }}>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#333333",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem sx={{ mt: 2, px: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleContactClick}
                sx={{
                  backgroundColor: "#6366f1",
                  borderRadius: "25px",
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Contact Us
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
